/**
 * Enhanced analytics tracking with debouncing and deduplication.
 *
 * This module provides a drop-in replacement for the standard track function
 * that includes spam protection and deduplication.
 */

import { debounce } from 'lodash-es';
import { getDebounceConfig, shouldDebounceEvent } from '../config/debounce-config.js';
import type { EventCategory } from '../constants/event-categories.js';
import type { EventName } from '../constants/event-names.js';
import { track as originalTrack } from '../track.js';
import type { AnalyticsEvent, EventProperties } from '../types/index.js';
import { processEventDeduplication } from './event-deduplication.js';

/**
 * Map to store debounced functions per event type
 */
const debouncedFunctions = new Map<string, ReturnType<typeof debounce>>();

/**
 * Metrics for tracking debounce effectiveness
 */
interface DebounceMetrics {
  totalCalls: number;
  debouncedCalls: number;
  duplicateBlocked: number;
  immediateTracked: number;
  debouncedTracked: number;
}

const metrics: DebounceMetrics = {
  totalCalls: 0,
  debouncedCalls: 0,
  duplicateBlocked: 0,
  immediateTracked: 0,
  debouncedTracked: 0
};

/**
 * Track an event immediately with deduplication
 */
async function trackImmediate(event: AnalyticsEvent): Promise<void> {
  // Process through deduplication
  if (!processEventDeduplication(event)) {
    metrics.duplicateBlocked++;
    return;
  }

  // Track the event
  await originalTrack(event);
  metrics.immediateTracked++;
}

/**
 * Get or create a debounced function for a specific event type
 */
function getDebouncedFunction(eventName: string): ReturnType<typeof debounce> {
  if (!debouncedFunctions.has(eventName)) {
    const config = getDebounceConfig(eventName);

    const debouncedFn = debounce(
      async (event: AnalyticsEvent) => {
        // Process through deduplication
        if (!processEventDeduplication(event)) {
          metrics.duplicateBlocked++;
          return;
        }

        // Track the event
        await originalTrack(event);
        metrics.debouncedTracked++;
      },
      config.debounce,
      {
        leading: false,
        trailing: true
      }
    );

    debouncedFunctions.set(eventName, debouncedFn);
  }

  return debouncedFunctions.get(eventName)!;
}

/**
 * Enhanced track function with debouncing and deduplication
 */
export async function track(
  name: EventName,
  category: EventCategory,
  properties?: EventProperties
): Promise<void> {
  metrics.totalCalls++;

  const event: AnalyticsEvent = {
    name,
    category,
    ...(properties && { properties }),
    timestamp: Date.now()
  } as AnalyticsEvent;

  // Check if this event type should be debounced
  if (!shouldDebounceEvent(name)) {
    await trackImmediate(event);
    return;
  }

  // Use debounced function for this event type
  const debouncedFn = getDebouncedFunction(name);
  metrics.debouncedCalls++;
  debouncedFn(event);
}

/**
 * Track an event immediately, bypassing debouncing but still using deduplication
 */
export async function trackNow(
  name: EventName,
  category: EventCategory,
  properties?: EventProperties
): Promise<void> {
  metrics.totalCalls++;

  const event: AnalyticsEvent = {
    name,
    category,
    ...(properties && { properties }),
    timestamp: Date.now()
  } as AnalyticsEvent;

  await trackImmediate(event);
}

/**
 * Flush all pending debounced events
 */
export function flushPendingEvents(): void {
  for (const debouncedFn of debouncedFunctions.values()) {
    debouncedFn.flush();
  }
}

/**
 * Cancel all pending debounced events
 */
export function cancelPendingEvents(): void {
  for (const debouncedFn of debouncedFunctions.values()) {
    debouncedFn.cancel();
  }
}

/**
 * Get debounce metrics
 */
export function getDebounceMetrics(): DebounceMetrics {
  return { ...metrics };
}

/**
 * Reset debounce metrics
 */
export function resetDebounceMetrics(): void {
  metrics.totalCalls = 0;
  metrics.debouncedCalls = 0;
  metrics.duplicateBlocked = 0;
  metrics.immediateTracked = 0;
  metrics.debouncedTracked = 0;
}

/**
 * Get information about configured debounced functions
 */
export function getDebounceInfo(): Array<{
  eventName: string;
  debounceDelay: number;
  dedupeWindow: number;
  hasPendingCalls: boolean;
}> {
  const result: Array<{
    eventName: string;
    debounceDelay: number;
    dedupeWindow: number;
    hasPendingCalls: boolean;
  }> = [];

  for (const [eventName, debouncedFn] of debouncedFunctions.entries()) {
    const config = getDebounceConfig(eventName);
    result.push({
      eventName,
      debounceDelay: config.debounce,
      dedupeWindow: config.dedupe,
      hasPendingCalls: (debouncedFn as any).pending || false
    });
  }

  return result;
}

/**
 * Clear all debounced functions (useful for testing)
 */
export function clearDebouncedFunctions(): void {
  cancelPendingEvents();
  debouncedFunctions.clear();
}

// Export the enhanced track function as default for easy replacement
export default track;
